/**
 * 診断コマンドの実装。
 *
 * 【最重要】ここの出力はハードコードしない。
 * すべて NetworkWorld の現在の状態から計算する。
 * ケーブルを抜けば ping は落ち、挿し直せば通る。
 */

import type {
  Device,
  DeviceId,
  DnsLookupResult,
  NetworkWorld,
  PingResult,
  TracerouteHop,
} from '../types/network';
import { evaluatePathFirewalls, type FirewallDecision, type Protocol } from './firewall';
import {
  APIPA_MASK,
  isApipa,
  isValidIpv4,
  maskToPrefix,
  networkAddress,
  sameSubnet,
  toCidr,
} from './ip';
import { createRng, defaultRng, hashString, type Rng } from './rng';
import {
  congestionHopCostMs,
  congestionLossRateForPath,
  linkUtilization,
  type LinkUtilizationMap,
} from './traffic';
import { wirelessHopCostMs, wirelessLossRateForPath } from './wireless';
import {
  findDeviceByIp,
  findDeviceByName,
  findDevicesByIp,
  findPath,
  isDeviceOperational,
  linksAlongPath,
  upLinksOf,
} from './topology';

// ---------------------------------------------------------------
// IP 設定の導出
// ---------------------------------------------------------------

export interface EffectiveIp {
  /** 実際に使えるアドレス。APIPA を掴んでいる場合は 169.254.x.y が入る */
  address: string | null;
  subnetMask: string;
  gateway: string | null;
  dnsServers: string[];
  dhcp: boolean;
  /** DHCP に失敗して APIPA を自動設定している状態か */
  apipa: boolean;
  /** 通信に使えるアドレスを持っているか（APIPA は「持っていない」扱い） */
  usable: boolean;
}

/**
 * DHCP でアドレスを取得できていない端末は、実機と同じく
 * 169.254.x.y / 255.255.0.0 の APIPA を自分で決める。
 * 値は機器 ID から決定論的に導出する（毎回同じアドレスになる）。
 */
export function apipaAddressFor(deviceId: DeviceId): string {
  const h = hashString(deviceId);
  const third = (h >>> 8) & 0xff;
  const fourth = h & 0xff;
  // 169.254.0.x と 169.254.255.x は予約されているため 1..254 に収める
  const safeThird = Math.min(254, Math.max(1, third));
  const safeFourth = Math.min(254, Math.max(1, fourth));
  return `169.254.${safeThird}.${safeFourth}`;
}

/** 機器の「実際に効いている」IP 設定を導出する */
export function getEffectiveIp(world: NetworkWorld, deviceId: DeviceId): EffectiveIp | null {
  const device = world.devices[deviceId];
  if (!device) return null;

  const raw = device.ip;
  if (raw.address === null && raw.dhcp) {
    const address = apipaAddressFor(deviceId);
    return {
      address,
      subnetMask: APIPA_MASK,
      gateway: null,
      dnsServers: [],
      dhcp: true,
      apipa: true,
      usable: false,
    };
  }

  const apipa = isApipa(raw.address);
  return {
    address: raw.address,
    subnetMask: raw.subnetMask,
    gateway: raw.gateway,
    dnsServers: raw.dnsServers ?? [],
    dhcp: raw.dhcp,
    apipa,
    usable: raw.address !== null && !apipa,
  };
}

export interface IpConfigResult {
  deviceId: DeviceId;
  label: string;
  dhcp: boolean;
  address: string | null;
  subnetMask: string;
  gateway: string | null;
  dnsServers: string[];
  apipa: boolean;
  /** 少なくとも 1 本、リンクアップしているケーブルがあるか */
  mediaConnected: boolean;
  networkAddress: string | null;
  cidr: string | null;
  macAddress: string;
  /** 画面に出す注意書き（APIPA、ケーブル未接続など） */
  notes: string[];
}

/** 機器 ID から MAC アドレス風の文字列を決定論的に作る（見た目のリアリティ用） */
export function macAddressFor(deviceId: DeviceId): string {
  const h = hashString(`mac:${deviceId}`);
  const b1 = (h >>> 16) & 0xff;
  const b2 = (h >>> 8) & 0xff;
  const b3 = h & 0xff;
  const hex = (n: number) => n.toString(16).toUpperCase().padStart(2, '0');
  return `00-15-5D-${hex(b1)}-${hex(b2)}-${hex(b3)}`;
}

/** ipconfig 相当。DHCP 失敗時は APIPA を返す挙動を再現する */
export function ipconfig(world: NetworkWorld, deviceId: DeviceId): IpConfigResult | null {
  const device = world.devices[deviceId];
  const eff = getEffectiveIp(world, deviceId);
  if (!device || !eff) return null;

  const mediaConnected = upLinksOf(world, deviceId).length > 0;
  const notes: string[] = [];
  if (!device.powered) notes.push('この機器の電源が入っていません。');
  if (!mediaConnected) notes.push('メディアは接続されていません（ケーブルが抜けているか、リンクが上がっていません）。');
  if (eff.apipa) {
    notes.push(
      'DHCP サーバーからアドレスを受け取れなかったため、自動プライベート IP アドレス (APIPA) を割り当てています。この状態では社内ネットワークに出られません。',
    );
  }
  if (eff.address === null) notes.push('IP アドレスが設定されていません。');
  if (device.quarantined) notes.push('この機器はネットワークから隔離されています。');

  const canCompute = eff.address !== null && isValidIpv4(eff.address) && isValidIpv4(eff.subnetMask);

  return {
    deviceId,
    label: device.label,
    dhcp: eff.dhcp,
    address: eff.address,
    subnetMask: eff.subnetMask,
    gateway: eff.gateway,
    dnsServers: eff.dnsServers,
    apipa: eff.apipa,
    mediaConnected,
    networkAddress: canCompute ? networkAddress(eff.address as string, eff.subnetMask) : null,
    cidr: canCompute ? toCidr(eff.address as string, eff.subnetMask) : null,
    macAddress: macAddressFor(deviceId),
    notes,
  };
}

// ---------------------------------------------------------------
// 名前解決
// ---------------------------------------------------------------

function lookupRecord(device: Device, hostname: string): string | null {
  if (!device.dnsRecords) return null;
  const needle = hostname.trim().toLowerCase().replace(/\.$/, '');
  for (const key of Object.keys(device.dnsRecords)) {
    if (key.trim().toLowerCase().replace(/\.$/, '') === needle) {
      return device.dnsRecords[key];
    }
  }
  return null;
}

/**
 * nslookup 相当。
 * DNS サーバーへ「実際に到達できるか」を確認したうえでレコードを引く。
 * サーバーに届かなければ 'server-unreachable'（章 3 の切り分けの肝）。
 */
export function nslookup(
  world: NetworkWorld,
  fromDeviceId: DeviceId,
  hostname: string,
): DnsLookupResult {
  const base: DnsLookupResult = {
    hostname,
    serverIp: null,
    address: null,
    failureReason: 'no-server',
  };

  const device = world.devices[fromDeviceId];
  if (!device) return base;

  // 'no-server' は「DNS サーバーが 1 つも設定されていない」ことだけを指す。
  // 自分に IP が無い場合は設定そのものは存在するので、判定には機器の設定値を直接見る
  // （getEffectiveIp は APIPA のとき dnsServers を空にして返すため、ここでは使えない）。
  const servers = (device.ip.dnsServers ?? []).filter((s) => isValidIpv4(s));
  if (servers.length === 0) return base;

  base.serverIp = servers[0];

  // 設定はあるが自分が通信できる状態でなければ、サーバーへは届かない
  const eff = getEffectiveIp(world, fromDeviceId);
  if (!eff || !eff.usable) {
    return { ...base, failureReason: 'server-unreachable' };
  }

  let anyReachable = false;
  for (const serverIp of servers) {
    const serverDevice = findDeviceByIp(world, serverIp);
    if (!serverDevice) continue;
    const reach = analyzeReachability(world, fromDeviceId, serverDevice.id, {
      dstPort: 53,
      protocol: 'udp',
    });
    if (!reach.reachable) continue;

    anyReachable = true;
    const address = lookupRecord(serverDevice, hostname);
    if (address !== null) {
      return { hostname, serverIp, address, failureReason: null };
    }
    // 届いたがレコードが無い → NXDOMAIN
    return { hostname, serverIp, address: null, failureReason: 'nxdomain' };
  }

  return {
    ...base,
    failureReason: anyReachable ? 'nxdomain' : 'server-unreachable',
  };
}

/** 名前解決が成功するか（Condition の canResolve 用） */
export function canResolve(world: NetworkWorld, fromDeviceId: DeviceId, hostname: string): boolean {
  return nslookup(world, fromDeviceId, hostname).failureReason === null;
}

// ---------------------------------------------------------------
// 到達性の解析
// ---------------------------------------------------------------

export type FailureReason = PingResult['failureReason'];

export interface ReachabilityOptions {
  /** TCP/UDP のポート番号を指定するとファイアウォール判定に使われる */
  dstPort?: number | null;
  protocol?: Protocol;
}

export interface ReachabilityResult {
  reachable: boolean;
  reason: FailureReason;
  /** 通信が成立する場合の経路（機器 ID の並び） */
  path: DeviceId[] | null;
  /** リンク状態を無視した「配線図どおりの経路」。切り分け説明に使う */
  intendedPath: DeviceId[] | null;
  srcIp: string | null;
  dstIp: string | null;
  /** 遮断された場合のファイアウォール判定 */
  firewall: FirewallDecision | null;
  /** 別サブネット宛でゲートウェイを経由した場合、そのゲートウェイ機器 */
  gatewayDeviceId: DeviceId | null;
}

function failure(
  reason: FailureReason,
  partial: Partial<ReachabilityResult> = {},
): ReachabilityResult {
  return {
    reachable: false,
    reason,
    path: null,
    intendedPath: null,
    srcIp: null,
    dstIp: null,
    firewall: null,
    gatewayDeviceId: null,
    ...partial,
  };
}

/**
 * ある機器から別の機器へ通信が成立するかを、理由付きで解析する。
 *
 * - 同一サブネット同士なら L2 到達性（＝経路が存在すること）だけを見る
 * - サブネットが違うならデフォルトゲートウェイ経由を要求する
 *   （ゲートウェイ未設定・到達不能・経路がゲートウェイを通らない場合は no-route）
 * - 経路上にファイアウォールがあればルールを評価する
 */
export function analyzeReachability(
  world: NetworkWorld,
  fromDeviceId: DeviceId,
  toDeviceId: DeviceId,
  options: ReachabilityOptions = {},
): ReachabilityResult {
  const from = world.devices[fromDeviceId];
  const to = world.devices[toDeviceId];
  if (!from || !to) return failure('no-route');

  if (from.quarantined) return failure('quarantined');
  if (!from.powered) return failure('no-ip');

  const srcEff = getEffectiveIp(world, fromDeviceId);
  if (!srcEff || !srcEff.usable || srcEff.address === null) {
    return failure('no-ip', { srcIp: srcEff?.address ?? null });
  }
  const srcIp = srcEff.address;

  if (to.quarantined) return failure('quarantined', { srcIp });
  if (!to.powered) return failure('host-down', { srcIp });

  const dstEff = getEffectiveIp(world, toDeviceId);
  const dstIp = dstEff?.address ?? null;
  if (!dstEff || dstIp === null) {
    return failure('no-route', { srcIp });
  }

  if (fromDeviceId === toDeviceId) {
    return {
      reachable: true,
      reason: null,
      path: [fromDeviceId],
      intendedPath: [fromDeviceId],
      srcIp,
      dstIp,
      firewall: null,
      gatewayDeviceId: null,
    };
  }

  const intendedPath = findPath(world, fromDeviceId, toDeviceId, {
    ignoreLinkState: true,
    ignoreDeviceState: true,
  });
  let path = findPath(world, fromDeviceId, toDeviceId);

  if (!path) {
    // 配線図上は繋がっているのに通らない ＝ リンクが落ちている
    const reason: FailureReason = intendedPath ? 'link-down' : 'no-route';
    return failure(reason, { srcIp, dstIp, intendedPath });
  }

  let gatewayDeviceId: DeviceId | null = null;

  const isSameSubnet = sameSubnet(srcIp, dstIp, srcEff.subnetMask);
  if (!isSameSubnet) {
    // 別サブネット宛 → デフォルトゲートウェイが要る
    if (!srcEff.gateway) {
      return failure('no-route', { srcIp, dstIp, path, intendedPath });
    }
    const gatewayDevice = findDeviceByIp(world, srcEff.gateway);
    if (!gatewayDevice || !isDeviceOperational(gatewayDevice)) {
      return failure('no-route', { srcIp, dstIp, path, intendedPath });
    }
    // ゲートウェイは自分と同じサブネットに居なければならない
    if (!sameSubnet(srcIp, srcEff.gateway, srcEff.subnetMask)) {
      return failure('no-route', { srcIp, dstIp, path, intendedPath });
    }
    // 実際の経路がゲートウェイを通っていること
    // 部署別インターフェースを持つルーターへは、同じコアスイッチを往復して転送する。
    if (!path.includes(gatewayDevice.id) && gatewayDevice.ipInterfaces?.length) {
      const toGateway = findPath(world, fromDeviceId, gatewayDevice.id);
      const fromGateway = findPath(world, gatewayDevice.id, toDeviceId);
      if (!toGateway || !fromGateway) {
        return failure('link-down', { srcIp, dstIp, intendedPath });
      }
      path = [...toGateway, ...fromGateway.slice(1)];
    }
    if (!path.includes(gatewayDevice.id)) {
      return failure('no-route', { srcIp, dstIp, path, intendedPath });
    }
    gatewayDeviceId = gatewayDevice.id;

    // 応答が戻ってくるためには相手側にも経路が要る
    const dstNeedsGateway =
      to.kind !== 'internet' && !sameSubnet(dstIp, srcIp, dstEff.subnetMask);
    if (dstNeedsGateway && !dstEff.gateway) {
      return failure('no-route', { srcIp, dstIp, path, intendedPath, gatewayDeviceId });
    }
  }

  const firewall = evaluatePathFirewalls(
    world,
    path,
    srcIp,
    dstIp,
    options.dstPort ?? null,
    options.protocol ?? 'icmp',
  );
  if (!firewall.allowed) {
    return failure('blocked', { srcIp, dstIp, path, intendedPath, firewall, gatewayDeviceId });
  }

  return {
    reachable: true,
    reason: null,
    path,
    intendedPath,
    srcIp,
    dstIp,
    firewall,
    gatewayDeviceId,
  };
}

/** 疎通判定（Condition の canReach 用） */
export function canReach(
  world: NetworkWorld,
  fromDeviceId: DeviceId,
  toDeviceId: DeviceId,
  options: ReachabilityOptions = {},
): boolean {
  return analyzeReachability(world, fromDeviceId, toDeviceId, options).reachable;
}

/** 指定した TCP ポートに接続できるか（章 5 のファイアウォール回で使う） */
export function canReachPort(
  world: NetworkWorld,
  fromDeviceId: DeviceId,
  toDeviceId: DeviceId,
  port: number,
): boolean {
  const to = world.devices[toDeviceId];
  if (!to) return false;
  const listening = to.listeningPorts ?? [];
  if (listening.length > 0 && !listening.includes(port)) return false;
  return canReach(world, fromDeviceId, toDeviceId, { dstPort: port, protocol: 'tcp' });
}

// ---------------------------------------------------------------
// ターゲット解決（IP でもホスト名でも受け付ける）
// ---------------------------------------------------------------

export interface TargetResolution {
  /** 入力そのまま */
  input: string;
  /** 名前解決後の IP。解決できなければ null */
  ip: string | null;
  /** 対応する機器 */
  device: Device | null;
  /** 名前解決に失敗したか */
  dnsFailed: boolean;
  dns: DnsLookupResult | null;
}

/**
 * IP がどの機器のものか特定する。
 * 社外のアドレス（どの機器の IP とも一致せず、社内サブネットにも属さない）は
 * インターネットノードが応答するものとして扱う。
 */
export function deviceForIp(world: NetworkWorld, ip: string): Device | null {
  const exact = findDeviceByIp(world, ip);
  if (exact) return exact;

  const localSubnets = Object.keys(world.devices)
    .map((id) => world.devices[id])
    .filter((d) => d.ip.address && isValidIpv4(d.ip.address) && isValidIpv4(d.ip.subnetMask));

  const isLocal = localSubnets.some((d) =>
    sameSubnet(ip, d.ip.address as string, d.ip.subnetMask),
  );
  if (isLocal) return null;

  const internet = Object.keys(world.devices)
    .map((id) => world.devices[id])
    .find((d) => d.kind === 'internet');
  return internet ?? null;
}

/** ping / traceroute の宛先（IP でもホスト名でも可）を解決する */
export function resolveTarget(
  world: NetworkWorld,
  fromDeviceId: DeviceId,
  target: string,
): TargetResolution {
  const trimmed = target.trim();
  if (isValidIpv4(trimmed)) {
    return {
      input: trimmed,
      ip: trimmed,
      device: deviceForIp(world, trimmed),
      dnsFailed: false,
      dns: null,
    };
  }

  // 機器 ID / 表示名で直接指定された場合（ゲーム内の利便性のため許可する）
  const byName = findDeviceByName(world, trimmed);
  if (byName) {
    const eff = getEffectiveIp(world, byName.id);
    return { input: trimmed, ip: eff?.address ?? null, device: byName, dnsFailed: false, dns: null };
  }

  const dns = nslookup(world, fromDeviceId, trimmed);
  if (dns.failureReason !== null || dns.address === null) {
    return { input: trimmed, ip: null, device: null, dnsFailed: true, dns };
  }
  return {
    input: trimmed,
    ip: dns.address,
    device: deviceForIp(world, dns.address),
    dnsFailed: false,
    dns,
  };
}

// ---------------------------------------------------------------
// RTT の計算
// ---------------------------------------------------------------

/** リンク速度に応じた 1 ホップあたりの遅延（ms） */
function hopCost(speedMbps: number): number {
  if (speedMbps >= 1000) return 0.25;
  if (speedMbps >= 100) return 0.5;
  if (speedMbps >= 10) return 1.8;
  return 0.6;
}

/**
 * 経路のホップ数から RTT を算出する。
 * インターネットを経由するホップは大きめの遅延を加える。
 *
 * load を渡すとリンク使用率の計算を使い回す（1 回の ping で 4 回呼ばれるため）。
 * 省略した場合はその場で計算するので、単体で呼んでも結果は同じ。
 */
export function computeRttMs(
  world: NetworkWorld,
  path: DeviceId[],
  rng: Rng = defaultRng,
  flags?: Record<string, boolean>,
  load?: LinkUtilizationMap,
): number {
  if (path.length <= 1) return round1(0.1 + rng() * 0.2);

  const linkIds = linksAlongPath(world, path);
  const utilization = load ?? linkUtilization(world);
  let total = 0.2;
  for (let i = 0; i + 1 < path.length; i++) {
    const linkId = linkIds[i];
    const link = linkId ? world.links[linkId] : undefined;
    const port = link
      ? world.devices[link.a.deviceId]?.ports.find((p) => p.index === link.a.portIndex)
      : undefined;
    const speed = port && port.speedMbps > 0 ? port.speedMbps : 100;
    total += hopCost(speed);
    // 無線リンクは電波品質から遅延を求める。
    // 干渉が強いほど再送が増えて遅くなる（章 4 の「遅い」を作っているのはここ）。
    if (link?.wireless) total += wirelessHopCostMs(world, link, flags);
    // 混んでいるリンクは待ち行列が伸びて遅くなる（章 6 の「全社が重い」を作っているのはここ）。
    // 「その機器が大量通信しているか」ではなく **そのリンクがどれだけ埋まっているか** で決める。
    // だから犯人から遠い席の人も、同じ 1 本を通っていれば同じだけ遅くなる。
    total += congestionHopCostMs(world, linkId, utilization);

    const nextDevice = world.devices[path[i + 1]];
    if (nextDevice?.kind === 'internet') total += 9;
    if (nextDevice?.kind === 'router' || nextDevice?.kind === 'firewall') total += 0.4;
  }

  // ±15% の揺らぎ
  const jitter = 0.85 + rng() * 0.3;
  return round1(Math.max(0.1, total * jitter));
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// ---------------------------------------------------------------
// ping
// ---------------------------------------------------------------

export interface PingOptions {
  /** 送信回数。既定は 4 */
  count?: number;
  rng?: Rng;
  /** 到達していても一定確率でパケットを落とす（章 6 の輻輳表現）。0..1 */
  lossRate?: number;
  /**
   * 進行フラグ。干渉源の有効・無効（電子レンジが回っているか等）の判定に使う。
   * 省略した場合は bindWorldFlags() で world に結び付けられたものを見る。
   */
  flags?: Record<string, boolean>;
}

/**
 * ping 相当。target は IP でもホスト名でも受け付ける。
 * RTT は経路のホップ数から算出し、わずかに乱数を乗せる。
 */
export function ping(
  world: NetworkWorld,
  fromDeviceId: DeviceId,
  target: string,
  options: PingOptions = {},
): PingResult {
  const count = options.count ?? 4;
  const rng = options.rng ?? defaultRng;

  const emptyReplies = (): (number | null)[] => new Array(count).fill(null);

  const from = world.devices[fromDeviceId];
  if (!from) {
    return {
      from: fromDeviceId,
      target,
      resolvedIp: null,
      replies: emptyReplies(),
      failureReason: 'no-route',
    };
  }

  // 自分側の状態を先に見る（名前解決より前に「IP が無い」を伝えたい）
  if (from.quarantined) {
    return {
      from: fromDeviceId,
      target,
      resolvedIp: null,
      replies: emptyReplies(),
      failureReason: 'quarantined',
    };
  }
  const srcEff = getEffectiveIp(world, fromDeviceId);
  if (!from.powered || !srcEff || !srcEff.usable) {
    return {
      from: fromDeviceId,
      target,
      resolvedIp: null,
      replies: emptyReplies(),
      failureReason: 'no-ip',
    };
  }

  const resolution = resolveTarget(world, fromDeviceId, target);
  if (resolution.dnsFailed || resolution.ip === null) {
    return {
      from: fromDeviceId,
      target,
      resolvedIp: null,
      replies: emptyReplies(),
      failureReason: 'dns-failed',
    };
  }

  if (!resolution.device) {
    return {
      from: fromDeviceId,
      target,
      resolvedIp: resolution.ip,
      replies: emptyReplies(),
      failureReason: 'no-route',
    };
  }

  const reach = analyzeReachability(world, fromDeviceId, resolution.device.id);
  if (!reach.reachable || !reach.path) {
    return {
      from: fromDeviceId,
      target,
      resolvedIp: resolution.ip,
      replies: emptyReplies(),
      failureReason: reach.reason,
    };
  }

  // 無線が荒れていれば、また経路のどこかが飽和していれば、
  // 指定が無くてもパケットは落ちる。
  // 「つながらない」ではなく「時々返ってこない・遅い」を作る。
  const utilization = linkUtilization(world);
  const lossRate = Math.max(
    options.lossRate ?? 0,
    wirelessLossRateForPath(world, reach.path, options.flags),
    congestionLossRateForPath(world, reach.path, utilization),
  );
  const replies: (number | null)[] = [];
  for (let i = 0; i < count; i++) {
    if (lossRate > 0 && rng() < lossRate) {
      replies.push(null);
      continue;
    }
    replies.push(computeRttMs(world, reach.path, rng, options.flags, utilization));
  }

  return {
    from: fromDeviceId,
    target,
    resolvedIp: resolution.ip,
    replies,
    failureReason: null,
  };
}

/** ping の統計（UI 表示用）。すべて replies から計算する */
export interface PingStats {
  sent: number;
  received: number;
  lost: number;
  lossPercent: number;
  minMs: number | null;
  maxMs: number | null;
  avgMs: number | null;
}

export function pingStats(result: PingResult): PingStats {
  const sent = result.replies.length;
  const values = result.replies.filter((r): r is number => r !== null);
  const received = values.length;
  const lost = sent - received;
  return {
    sent,
    received,
    lost,
    lossPercent: sent === 0 ? 0 : Math.round((lost / sent) * 100),
    minMs: received ? Math.min(...values) : null,
    maxMs: received ? Math.max(...values) : null,
    avgMs: received ? round1(values.reduce((a, b) => a + b, 0) / received) : null,
  };
}

// ---------------------------------------------------------------
// traceroute
// ---------------------------------------------------------------

/** L3 のホップとして表示すべき機器か（スイッチは traceroute に出てこない） */
function isL3Hop(device: Device | undefined): boolean {
  if (!device) return false;
  return device.kind === 'router' || device.kind === 'firewall' || device.kind === 'internet';
}

export interface TracerouteOptions {
  rng?: Rng;
  maxHops?: number;
  /** ping と同じ。干渉源の有効・無効の判定に使う */
  flags?: Record<string, boolean>;
}

/**
 * traceroute 相当。
 * 経路上のルーター／ファイアウォールと最終宛先だけをホップとして表示する
 * （スイッチは L2 機器なので TTL を減らさず、実機でも表示されない）。
 * 途中で止まっている場合は、届く所までを表示して最後に「*」（タイムアウト）を出す。
 */
export function traceroute(
  world: NetworkWorld,
  fromDeviceId: DeviceId,
  target: string,
  options: TracerouteOptions = {},
): TracerouteHop[] {
  const rng = options.rng ?? createRng(hashString(`${fromDeviceId}:${target}`));
  const maxHops = options.maxHops ?? 15;

  const from = world.devices[fromDeviceId];
  if (!from) return [];

  const srcEff = getEffectiveIp(world, fromDeviceId);
  if (from.quarantined || !from.powered || !srcEff || !srcEff.usable) {
    return [{ hop: 1, deviceLabel: null, ip: null, rttMs: null }];
  }

  const resolution = resolveTarget(world, fromDeviceId, target);
  if (resolution.dnsFailed || resolution.ip === null || !resolution.device) {
    return [{ hop: 1, deviceLabel: null, ip: null, rttMs: null }];
  }

  const targetDeviceId = resolution.device.id;
  const reach = analyzeReachability(world, fromDeviceId, targetDeviceId);

  // 実経路が無ければ配線図どおりの経路を辿り、届かなくなった所で打ち切る
  const walkPath =
    reach.path ??
    findPath(world, fromDeviceId, targetDeviceId, {
      ignoreLinkState: true,
      ignoreDeviceState: true,
    });

  if (!walkPath) {
    return [{ hop: 1, deviceLabel: null, ip: null, rttMs: null }];
  }

  const hops: TracerouteHop[] = [];
  let hopNumber = 0;
  // 混雑の計算はホップごとに変わらないので 1 回だけ求めて使い回す
  const utilization = linkUtilization(world);

  for (let i = 1; i < walkPath.length && hopNumber < maxHops; i++) {
    const deviceId = walkPath[i];
    const device = world.devices[deviceId];
    const isLast = deviceId === targetDeviceId;
    if (!isL3Hop(device) && !isLast) continue;

    hopNumber++;
    const hopReach = analyzeReachability(world, fromDeviceId, deviceId);
    if (hopReach.reachable && hopReach.path) {
      const eff = getEffectiveIp(world, deviceId);
      hops.push({
        hop: hopNumber,
        deviceLabel: device?.label ?? null,
        ip: isLast ? resolution.ip : device.ipInterfaces?.find(iface =>
          srcEff.address && sameSubnet(srcEff.address, iface.address, iface.subnetMask),
        )?.address ?? eff?.address ?? null,
        rttMs: computeRttMs(world, hopReach.path, rng, options.flags, utilization),
      });
    } else {
      hops.push({ hop: hopNumber, deviceLabel: null, ip: null, rttMs: null });
      break;
    }
  }

  if (hops.length === 0) {
    hops.push({ hop: 1, deviceLabel: null, ip: null, rttMs: null });
  }
  return hops;
}

// ---------------------------------------------------------------
// IP アドレス重複の検出（章 2）
// ---------------------------------------------------------------

export interface IpConflict {
  address: string;
  deviceIds: DeviceId[];
  deviceLabels: string[];
  /** 同一の L2 セグメントに居るか（実際に通信障害を起こす組み合わせか） */
  sameSegment: boolean;
}

/**
 * 同一 IP を持つ機器を検出する。
 * APIPA と未設定は対象外。電源が入っていない機器も対象外（通信していないため）。
 */
export function detectIpConflict(world: NetworkWorld): IpConflict[] {
  const byAddress = new Map<string, DeviceId[]>();

  for (const id of Object.keys(world.devices)) {
    const device = world.devices[id];
    if (!device.powered) continue;
    const eff = getEffectiveIp(world, id);
    if (!eff || !eff.usable || eff.address === null) continue;
    const list = byAddress.get(eff.address) ?? [];
    list.push(id);
    byAddress.set(eff.address, list);
  }

  const conflicts: IpConflict[] = [];
  for (const [address, deviceIds] of byAddress) {
    if (deviceIds.length < 2) continue;
    const sameSegment = deviceIds.some((a, i) =>
      deviceIds.slice(i + 1).some((b) => findPath(world, a, b) !== null),
    );
    conflicts.push({
      address,
      deviceIds,
      deviceLabels: deviceIds.map((id) => world.devices[id]?.label ?? id),
      sameSegment,
    });
  }
  return conflicts;
}

/** 指定した機器の IP が他機器と重複しているか */
export function hasIpConflict(world: NetworkWorld, deviceId: DeviceId): boolean {
  const eff = getEffectiveIp(world, deviceId);
  if (!eff || !eff.usable || eff.address === null) return false;
  return findDevicesByIp(world, eff.address).filter((d) => d.powered).length > 1;
}

/** 同じサブネットに属する機器の一覧（ネットワーク地図や学習表示に使う） */
export function devicesInSameSubnet(world: NetworkWorld, deviceId: DeviceId): Device[] {
  const eff = getEffectiveIp(world, deviceId);
  if (!eff || eff.address === null) return [];
  return Object.keys(world.devices)
    .map((id) => world.devices[id])
    .filter((d) => {
      if (d.id === deviceId) return false;
      const other = getEffectiveIp(world, d.id);
      if (!other || other.address === null) return false;
      return sameSubnet(eff.address as string, other.address, eff.subnetMask);
    });
}

/** サブネットの要約（学習用の表示に使う） */
export function subnetSummary(
  world: NetworkWorld,
  deviceId: DeviceId,
): { cidr: string; prefix: number; network: string } | null {
  const eff = getEffectiveIp(world, deviceId);
  if (!eff || eff.address === null || !isValidIpv4(eff.subnetMask)) return null;
  return {
    cidr: toCidr(eff.address, eff.subnetMask),
    prefix: maskToPrefix(eff.subnetMask),
    network: networkAddress(eff.address, eff.subnetMask),
  };
}
